import * as React from 'react';
import Icon from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { Button, Col, Divider, Row, Space } from 'antd';
import { useSize, useUpdateEffect } from 'ahooks';
import { cx } from '@emotion/css';
import { v4 as uuidV4 } from 'uuid';
import { BackButton, PreviousButton, SkipButton, stepTextMap } from './RouterButton';
import { Steps } from '../consts/step';
import { useStepName } from '../hooks/useStepName';
import { eventBus } from 'services/eventBus';
import useSystem from 'hooks/business/system/useSystem';
import { ReactComponent as TranslateSvg } from '../assets/translate.svg';
import { useTranslateMode } from '../atoms/translateMode';
import { cleanCache } from '../hooks/useConfiguration';
import { isUndefined } from 'lodash-es';
import dayjs from 'dayjs';
import posthog from '@leyan/lytics';

interface ConfigPanelProps extends React.ComponentProps<'div'> {
  onPrevious?: (() => void) | boolean;
  rightExtraNode?: React.ReactNode;
  children?: React.ReactNode;
  contentClassName?: React.ComponentProps<'div'>['className'];
  autoScroll?: boolean;
  beforeSkip?: () => Promise<any>;
  titleWithTiktok?: boolean; // title hardcode
}

export const Title = (props: { withTiktok?: boolean }) => {
  const { t } = useTranslate();
  const { withTiktok = false } = props;
  const stepName = useStepName();
  switch (stepName) {
    case Steps.WELCOME_MESSAGE:
      return <>{t('aide.进店欢迎语')}</>;
    case Steps.AUTO_REPLY_ON_TIMEOUT:
      return <>{t('nav.autoReply')}</>;
    case Steps.PROMPT_ORDER:
      return <>{t('marketing.催下单')}</>;
    case Steps.PROMPT_PAYMENT:
      return <>{t('ViewDashboard.催付款')}</>;
    case Steps.PROMPT_POSITIVE_REVIEW:
      return <>{withTiktok ? t('邀请评价') : t('ViewDashboard.催好评')}</>;
    case Steps.SHIPPING_DAYS:
      return <>{t('boot_ship_day_label')}</>;
    case Steps.LOGISTICS_CARE:
      return <>{t('aide.物流呵护')}</>;
    case Steps.ABANDONED_CART_RECOVERY:
      return <>{t('aide.弃单挽回')}</>;
    case Steps.ACTIVITY_START_PAGE:
    case Steps.FLASH_SALE_EVENT:
    case Steps.PRODUCT_DISCOUNT:
    case Steps.RECOMMENDED_HOT_ITEMS:
      return <>{t('yingxiaohuodong')}</>;
    case Steps.REVIEW_MANAGEMENT:
      return <>{t('aide.评价管理')}</>;
    case Steps.AI_MODEL_LOGISTICS:
      return <>{t('knowledge.发货与物流')}</>;
    case Steps.AI_MODEL_STORE:
      return <>{t('店铺服务')}</>;
    case Steps.MARKETING_WELCOME:
      return <>{`${t('aide.进店欢迎语')} & ${t('nav.autoReply')}`}</>;
    default:
      return null;
  }
};

function useStepLog() {
  const { t } = useTranslate();
  const stepName = useStepName();
  const stepText = stepTextMap[stepName];

  const refsCurrent = { t };
  const refs = React.useRef(refsCurrent);
  refs.current = refsCurrent;

  React.useEffect(() => {
    if (!stepName || !stepText) {
      return;
    }

    const start = Date.now();

    return () => {
      const end = Date.now();
      const duration = end - start;
      const hours = Math.floor(duration / 60 / 60 / 1000);
      const durationText = `${hours >= 10 ? hours : `0${hours}`}:${dayjs
        .duration(duration)
        .format('mm:ss')}`;
      posthog.capture('$bootGuideStepDuration', {
        start,
        end,
        duration,
        durationText,
        stepName,
        stepText,
      });
    };
  }, [stepText, stepName]);
}

const isDev = process.env.NODE_ENV === 'development';

export const ConfigPanel = (props: ConfigPanelProps) => {
  const {
    onPrevious = true,
    children,
    className,
    contentClassName,
    rightExtraNode,
    autoScroll = true,
    beforeSkip,
    titleWithTiktok = false,
    ...otherProps
  } = props;
  const [id, setId] = React.useState(uuidV4());
  const containerRef = React.useRef<HTMLDivElement>(null);
  const containerHeight = useSize(containerRef)?.height;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const contentHeight = useSize(contentRef)?.height;
  const contentFirstRender = React.useRef(false);

  const {
    systemSetting: { language: sysLang },
  } = useSystem();

  useUpdateEffect(() => {
    contentFirstRender.current = true;
  }, [contentHeight]);

  React.useEffect(() => {
    if (autoScroll && containerHeight && contentHeight && contentHeight > containerHeight) {
      if (contentFirstRender.current) {
        requestAnimationFrame(() => {
          containerRef.current?.scrollTo({
            behavior: 'smooth',
            top: contentHeight - containerHeight,
          });
        });
      }
    }
  }, [containerHeight, contentHeight, autoScroll]);

  useStepLog();

  return (
    <div className={cx('py-[24px] px-[72px] h-full', className)} {...otherProps}>
      <div className="bg-[#fff] shadow-[0px 2px 12px 0px rgba(167,178,204,0.10)] h-full flex flex-col flex-grow !focus:hover:bg-transparent">
        <Row
          className="px-[24px] basis-[50px] flex-shrink-0 border-0 border-b border-solid border-[#f0f2f5] text-[#515359]"
          align="middle"
        >
          <Col span={8} className="!flex items-center">
            <BackButton />
            <Divider type="vertical" className="!mx-[16px]" />
            {onPrevious && <PreviousButton />}
          </Col>
          <Col
            span={8}
            className="text-center text-[#232426] font-medium !flex items-center justify-center"
          >
            <Title withTiktok={titleWithTiktok} />
            {isDev && (
              <>
                <div
                  onClick={() => {
                    setId(uuidV4());
                  }}
                >
                  &nbsp;刷新
                </div>
                <div
                  onClick={() => {
                    eventBus.emit('GPT_GENERATOR_TRANS', sysLang);
                  }}
                >
                  &nbsp;翻译
                </div>
                <div onClick={cleanCache}>清理缓存</div>
              </>
            )}
          </Col>
          <Col span={8} className="!flex justify-end">
            {isUndefined(rightExtraNode) ? (
              <Space size={16}>
                <SkipButton beforeSkip={beforeSkip} />
                <TranslateButton />
              </Space>
            ) : (
              rightExtraNode
            )}
          </Col>
        </Row>
        <div
          className={cx('flex-grow overflow-auto', contentClassName)}
          key={id}
          ref={containerRef}
        >
          <div ref={contentRef}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export const TranslateButton = () => {
  const [active, setActive] = useTranslateMode();
  const { t } = useTranslate();
  return (
    <Button
      type={active ? 'link' : 'text'}
      icon={<Icon component={TranslateSvg} />}
      onClick={() => setActive(!active)}
      className="!px-0"
    >
      {t('phrase.翻译')}
    </Button>
  );
};
